/**
 * useObjectInfoLabels
 *
 * Renders the floating ID/IP/NAME labels under every element on the current
 * floor. Mirrors the architecture of useRelationshipOverlay:
 *   - one dedicated viewport SVG layer (so zoom/pan transforms apply for free)
 *   - never adds JointJS cells (graph.toJSON stays clean)
 *   - self-healing layer lookup on every render so we recover from the
 *     `.joint-layers` element not existing yet on first mount.
 *
 * Two effects:
 *   1. Full re-render — when paper/graph/floor/toggles change, clear the
 *      layer and rebuild every cell's label group.
 *   2. Incremental updates — subscribe to graph add/remove/change events so a
 *      drag, paste, or property edit doesn't force the whole floor to redraw.
 *
 * Cleanup: on unmount we detach the layer entirely (relationship overlay
 * does the same). Floor switches don't unmount this hook; the full re-render
 * effect handles them.
 */

import { useEffect, useRef } from 'react'
import { dia } from '@joint/core'
import {
  getOrCreateViewportLayer,
  clearLayer,
  removeLayer,
} from '../lib/paperViewportLayer'
import { extractLabelLines } from '../lib/extractLabelLines'
import {
  renderObjectLabelNode,
  LABEL_TOP_GAP,
  LABEL_FONT_SIZE,
} from '../lib/renderObjectLabelNode'
import { computeLabelFontSize } from '../lib/parkingScaleMetrics'
import { ObjectLabelToggles } from '../lib/objectLabelToggleStorage'

const OVERLAY_GROUP_ID = 'object-info-labels'

function isAnyToggleOn(t: ObjectLabelToggles): boolean {
  return t.id || t.ip || t.name
}

/**
 * Build the SVG <g> for one cell using extractLabelLines + renderObjectLabelNode.
 * Centralized here so both the full-rebuild effect and the incremental-update
 * effect produce identical results.
 */
function buildLabelForCell(
  cell: dia.Cell,
  toggles: ObjectLabelToggles,
  fontSize: number,
): SVGGElement | null {
  // Links don't have a usable bbox for "below the object" anchoring, and
  // labels for relationship endpoints are owned by useRelationshipOverlay.
  if (typeof (cell as dia.Element).getBBox !== 'function') return null
  const bbox = (cell as dia.Element).getBBox()
  if (!bbox) return null

  const lines = extractLabelLines(cell, toggles)
  if (lines.length === 0) return null

  // 객체 하단 여백도 폰트 크기에 비례 (기존 4px 폰트 ↔ 1px gap 비율).
  const topGap = fontSize * (LABEL_TOP_GAP / LABEL_FONT_SIZE)

  return renderObjectLabelNode({
    cellId: String(cell.id),
    lines,
    anchorX: bbox.x + bbox.width / 2,
    anchorY: bbox.y + bbox.height + topGap,
    fontSize,
    // No maxTextWidth: per user feedback (Iteration 3) labels must show
    // up to ~30-char ids in full instead of being compressed to the
    // object's footprint. Adjacent labels may visually overlap on very
    // dense maps; that trade-off is intentional.
  })
}

function removeLabelFromLayer(layer: SVGGElement, cellId: string): void {
  // CSS.escape protects against ids that contain selector-special characters
  // (UUIDs typically don't, but cell ids can be arbitrary strings).
  const existing = layer.querySelector(
    `[data-cell-id="${CSS.escape(cellId)}"]`,
  )
  if (existing) existing.remove()
}

/**
 * @param paper      Paper to attach the overlay layer to.
 * @param graph      Graph whose elements get labels.
 * @param floorId    Used as a render dependency so the layer rebuilds when
 *                   the floor changes (graph reference may not change but
 *                   contents do; this gives us a deterministic redraw key).
 * @param toggles    Which fields to show. When all are off, the overlay is
 *                   cleared so 5k-cell maps incur zero label-render cost.
 */
export function useObjectInfoLabels(
  paper: dia.Paper | null,
  graph: dia.Graph | null,
  floorId: string | null,
  toggles: ObjectLabelToggles,
): void {
  const layerRef = useRef<SVGGElement | null>(null)
  // 그래프 스케일(주차면 단변 중앙값) 기반 폰트 크기. full re-render 시 1회
  // 계산해 incremental add/change 핸들러에서 재사용한다.
  const fontSizeRef = useRef<number>(LABEL_FONT_SIZE)

  // Effect 1: full re-render. Runs on toggle change, floor change, paper or
  // graph change. We re-acquire the layer on every run so a paper that wasn't
  // ready on first mount eventually self-heals.
  useEffect(() => {
    if (!paper) return

    const layer = getOrCreateViewportLayer(paper, OVERLAY_GROUP_ID)
    if (!layer) return
    layerRef.current = layer
    layer.setAttribute('pointer-events', 'none')

    clearLayer(layer)

    if (!graph) return
    if (!isAnyToggleOn(toggles)) return // user turned everything off → leave layer empty

    fontSizeRef.current = computeLabelFontSize(graph)

    for (const cell of graph.getElements()) {
      const node = buildLabelForCell(cell, toggles, fontSizeRef.current)
      if (node) layer.appendChild(node)
    }
  }, [paper, graph, floorId, toggles])

  // Effect 2: incremental updates. Listening here instead of redoing the full
  // rebuild on every cell move keeps a 5k-cell drag interaction from rerunning
  // extractLabelLines for every untouched cell.
  useEffect(() => {
    if (!graph) return
    const layer = layerRef.current
    if (!layer) return

    // No toggles on → no label work needed; the full-rebuild effect already
    // emptied the layer. We still register listeners so the moment the user
    // toggles something on, the existing render path handles it.
    const enabled = isAnyToggleOn(toggles)

    const onAdd = (cell: dia.Cell) => {
      if (!enabled) return
      const node = buildLabelForCell(cell, toggles, fontSizeRef.current)
      if (node) layer.appendChild(node)
    }
    const onRemove = (cell: dia.Cell) => {
      removeLabelFromLayer(layer, String(cell.id))
    }
    const onChange = (cell: dia.Cell) => {
      // For a position/data change the simplest correct behavior is
      // "remove + re-add" — re-extracting lines and re-anchoring in one step.
      removeLabelFromLayer(layer, String(cell.id))
      if (!enabled) return
      const node = buildLabelForCell(cell, toggles, fontSizeRef.current)
      if (node) layer.appendChild(node)
    }
    // graph 'reset' fires when fromJSON replaces the entire model (floor
    // switch, dashboard re-entry). We don't repopulate here — the full-rebuild
    // effect above runs whenever floorId / graph identity changes, which
    // covers reset cases driven by floor switching. For in-place resets we
    // still clear stale labels so they don't dangle.
    const onReset = () => {
      clearLayer(layer)
    }

    graph.on('add', onAdd)
    graph.on('remove', onRemove)
    graph.on('change:position change:data', onChange)
    graph.on('reset', onReset)

    return () => {
      graph.off('add', onAdd)
      graph.off('remove', onRemove)
      graph.off('change:position change:data', onChange)
      graph.off('reset', onReset)
    }
  }, [graph, toggles])

  // Effect 3: detach the overlay <g> on unmount so React StrictMode /
  // navigation doesn't leak SVG nodes between mounts.
  useEffect(() => {
    return () => {
      const layer = layerRef.current
      if (layer) {
        removeLayer(layer)
        layerRef.current = null
      }
    }
  }, [])
}
